/**
 * RTK Query API Slice for System Administration
 * 
 * Handles all system administration endpoints including:
 * - HIPAA audit logs management
 * - System statistics and monitoring
 * - Cache management operations
 * - Active session monitoring
 * - System health checks
 * - Database backup operations
 * - System logs access
 * 
 * Key architectural decisions:
 * - Uses existing apiClient as baseQuery (maintains snake_case)
 * - System Admin role only (strict authorization)
 * - Minimal caching for real-time system monitoring
 * - HIPAA audit logging for all admin operations
 * - Long-running operations support
 * - Secure access to sensitive system data
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: apiClientBaseQuery,
  
  // Admin operations should have minimal caching for real-time monitoring
  keepUnusedDataFor: 60, // 1 minute for most admin data
  refetchOnMountOrArgChange: true, // Always refetch for data freshness
  refetchOnFocus: false, // Don't refetch on window focus
  
  // Define cache tags for invalidation
  tagTypes: ['AuditLog', 'SystemStats', 'ActiveSession', 'SystemHealth', 'SystemLog'],
  
  endpoints: (builder) => ({
    // Get HIPAA audit logs (critical for compliance)
    getAuditLogs: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/audit-logs',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          start_date: params.start_date,
          end_date: params.end_date,
          user_id: params.user_id,
          action_type: params.action_type,
          client_id: params.client_id,
          ip_address: params.ip_address,
          severity: params.severity,
          sort_by: params.sort_by || 'created_at',
          sort_order: params.sort_order || 'DESC',
          ...params,
        },
      }),
      transformResponse: (response) => {
        // Audit logs include pagination and detailed access records
        return response.data || { items: [], pagination: {} };
      },
      // Audit logs should not be cached for compliance
      keepUnusedDataFor: 0,
      providesTags: (result, error, params) => [
        { type: 'AuditLog', id: JSON.stringify(params) }
      ],
    }),

    // Get system statistics and metrics
    getSystemStats: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/system-stats',
        method: 'GET',
        params: {
          period: params.period || '24h', // 1h, 24h, 7d, 30d
          include_detailed: params.include_detailed || false,
          metrics: params.metrics || 'all', // cpu, memory, disk, database, api
        },
      }),
      transformResponse: (response) => {
        // System stats include performance metrics, usage statistics
        return response.data || {};
      },
      // System stats can be cached briefly
      keepUnusedDataFor: 30, // 30 seconds
      providesTags: ['SystemStats'],
    }),

    // Clear system cache (dangerous operation - admin only)
    clearCache: builder.mutation({
      query: (params = {}) => ({
        url: '/v1/admin/clear-cache',
        method: 'POST',
        data: {
          cache_type: params.cache_type || 'all', // all, redis, memory, file
          force: params.force || false,
          reason: params.reason, // Required for audit trail
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // Invalidate cache-related tags after clearing
      invalidatesTags: ['SystemStats', 'SystemHealth'],
    }),

    // Get active user sessions
    getActiveSessions: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/active-sessions',
        method: 'GET',
        params: {
          page: params.page || 1,
          limit: params.limit || 100,
          user_id: params.user_id,
          client_id: params.client_id,
          ip_address: params.ip_address,
          session_age: params.session_age, // minutes
          sort_by: params.sort_by || 'last_activity',
          include_expired: params.include_expired || false,
        },
      }),
      transformResponse: (response) => {
        // Active sessions include user details, login times, IP addresses
        return response.data || { sessions: [], total: 0 };
      },
      // Session data should be real-time
      keepUnusedDataFor: 0,
      providesTags: ['ActiveSession'],
    }),

    // Terminate user session (security operation)
    terminateSession: builder.mutation({
      query: (sessionData) => ({
        url: '/v1/admin/terminate-session',
        method: 'POST',
        data: {
          session_id: sessionData.session_id,
          user_id: sessionData.user_id,
          reason: sessionData.reason, // Required for audit trail
          force_logout: sessionData.force_logout || false,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // Invalidate session data after termination
      invalidatesTags: ['ActiveSession'],
    }),

    // Get system health check
    getSystemHealth: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/system-health',
        method: 'GET',
        params: {
          include_external: params.include_external || true, // Check Python API
          include_database: params.include_database || true,
          include_filesystem: params.include_filesystem || true,
          include_memory: params.include_memory || true,
          timeout: params.timeout || 30, // seconds
        },
      }),
      transformResponse: (response) => {
        // Health check includes status of all system components
        return response.data || { status: 'unknown', checks: [] };
      },
      // Health checks should have minimal caching
      keepUnusedDataFor: 15, // 15 seconds
      providesTags: ['SystemHealth'],
    }),

    // Backup database (long-running operation)
    backupDatabase: builder.mutation({
      query: (backupData) => ({
        url: '/v1/admin/backup-database',
        method: 'POST',
        data: {
          backup_type: backupData.backup_type || 'full', // full, incremental, schema
          include_logs: backupData.include_logs || false,
          compression: backupData.compression || 'gzip',
          destination: backupData.destination || 'default',
          retention_days: backupData.retention_days || 30,
          reason: backupData.reason, // Required for audit
        },
      }),
      transformResponse: (response) => {
        // Backup operation returns job ID for tracking
        return response.data || {};
      },
    }),

    // Get system logs
    getSystemLogs: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/logs',
        method: 'GET',
        params: {
          log_level: params.log_level || 'info', // error, warn, info, debug
          start_date: params.start_date,
          end_date: params.end_date,
          source: params.source, // server, database, external_api
          search: params.search,
          page: params.page || 1,
          limit: params.limit || 100,
          format: params.format || 'json', // json, text
        },
      }),
      transformResponse: (response) => {
        // System logs include structured log entries
        return response.data || { logs: [], total: 0 };
      },
      // Logs should not be cached for real-time monitoring
      keepUnusedDataFor: 0,
      providesTags: (result, error, params) => [
        { type: 'SystemLog', id: JSON.stringify(params) }
      ],
    }),

    // Get database statistics
    getDatabaseStats: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/database-stats',
        method: 'GET',
        params: {
          include_tables: params.include_tables || true,
          include_indexes: params.include_indexes || false,
          include_performance: params.include_performance || false,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // Database stats can be cached briefly
      keepUnusedDataFor: 60, // 1 minute
      providesTags: ['DatabaseStats'],
    }),

    // Get API usage statistics
    getApiUsageStats: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/api-usage',
        method: 'GET',
        params: {
          period: params.period || '24h',
          endpoint: params.endpoint,
          user_id: params.user_id,
          client_id: params.client_id,
          status_code: params.status_code,
          group_by: params.group_by || 'endpoint', // endpoint, user, client, hour
        },
      }),
      transformResponse: (response) => {
        return response.data || { usage: [], summary: {} };
      },
      keepUnusedDataFor: 60, // 1 minute
      providesTags: ['ApiUsageStats'],
    }),

    // Manage system maintenance mode
    setMaintenanceMode: builder.mutation({
      query: (maintenanceData) => ({
        url: '/v1/admin/maintenance-mode',
        method: 'POST',
        data: {
          enabled: maintenanceData.enabled,
          message: maintenanceData.message,
          duration: maintenanceData.duration, // minutes
          allow_admin_access: maintenanceData.allow_admin_access || true,
          scheduled_start: maintenanceData.scheduled_start,
          reason: maintenanceData.reason, // Required for audit
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      invalidatesTags: ['SystemHealth', 'SystemStats'],
    }),

    // Get maintenance mode status
    getMaintenanceStatus: builder.query({
      query: () => ({
        url: '/v1/admin/maintenance-mode',
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data || { enabled: false };
      },
      // Check maintenance status frequently
      keepUnusedDataFor: 30, // 30 seconds
      providesTags: ['MaintenanceStatus'],
    }),

    // Export audit logs for compliance
    exportAuditLogs: builder.mutation({
      query: (exportData) => ({
        url: '/v1/admin/export-audit-logs',
        method: 'POST',
        data: {
          format: exportData.format || 'csv', // csv, json, pdf
          start_date: exportData.start_date,
          end_date: exportData.end_date,
          filters: exportData.filters || {},
          include_sensitive: exportData.include_sensitive || false,
          encryption: exportData.encryption || false,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Get server configuration (sensitive data)
    getServerConfig: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/server-config',
        method: 'GET',
        params: {
          include_sensitive: params.include_sensitive || false,
          section: params.section, // database, email, external_api, security
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
      // Config should not be cached for security
      keepUnusedDataFor: 0,
    }),

    // Update server configuration
    updateServerConfig: builder.mutation({
      query: (configData) => ({
        url: '/v1/admin/server-config',
        method: 'PUT',
        data: {
          section: configData.section, // Required
          config: configData.config, // Section-specific config object
          restart_required: configData.restart_required || false,
          backup_current: configData.backup_current || true,
          reason: configData.reason, // Required for audit
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Monitor external API health
    getExternalApiHealth: builder.query({
      query: () => ({
        url: '/v1/admin/external-api-health',
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data || { python_api: { status: 'unknown' } };
      },
      // External API health should be checked frequently
      keepUnusedDataFor: 30, // 30 seconds
      providesTags: ['ExternalApiHealth'],
    }),

    // Get security alerts
    getSecurityAlerts: builder.query({
      query: (params = {}) => ({
        url: '/v1/admin/security-alerts',
        method: 'GET',
        params: {
          severity: params.severity, // low, medium, high, critical
          status: params.status || 'active', // active, resolved, acknowledged
          limit: params.limit || 50,
          since: params.since,
        },
      }),
      transformResponse: (response) => {
        return response.data || { alerts: [], summary: {} };
      },
      // Security alerts should be real-time
      keepUnusedDataFor: 0,
      providesTags: ['SecurityAlerts'],
    }),

    // Acknowledge security alert
    acknowledgeSecurityAlert: builder.mutation({
      query: (alertData) => ({
        url: `/v1/admin/security-alerts/${alertData.alert_id}/acknowledge`,
        method: 'PUT',
        data: {
          notes: alertData.notes,
          admin_user_id: alertData.admin_user_id,
        },
      }),
      invalidatesTags: ['SecurityAlerts'],
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetAuditLogsQuery,
  useGetSystemStatsQuery,
  useGetActiveSessionsQuery,
  useGetSystemHealthQuery,
  useGetSystemLogsQuery,
  useGetDatabaseStatsQuery,
  useGetApiUsageStatsQuery,
  useGetMaintenanceStatusQuery,
  useGetServerConfigQuery,
  useGetExternalApiHealthQuery,
  useGetSecurityAlertsQuery,
  
  // Mutations
  useClearCacheMutation,
  useTerminateSessionMutation,
  useBackupDatabaseMutation,
  useSetMaintenanceModeMutation,
  useExportAuditLogsMutation,
  useUpdateServerConfigMutation,
  useAcknowledgeSecurityAlertMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetAuditLogsQuery,
  useLazyGetSystemLogsQuery,
  useLazyGetSystemStatsQuery,
  
  // For manual cache invalidation if needed
  util: { invalidateTags },
} = adminApi;

export default adminApi;